import { fillTemplate, initFilterList } from "@scripts/filterList";
import type { ComponentInit } from "@scripts/registry";
import { headerOffset, scrollToElement } from "@scripts/scrollController";

import {
	ARTICLE_LISTING_ALL,
	ARTICLE_LISTING_PER_PAGE,
	pageCountFor,
} from "./ArticleListing.types";

const FILTERS = "[data-fa-article-listing-filters]";
const LIST = "[data-fa-article-listing-list]";
const ITEM = "[data-fa-article-listing-item]";
const STATUS = "[data-fa-article-listing-status]";
const PAGER = "[data-fa-pagination]";
const PAGE = "[data-fa-pagination-page]";
const STEP = "[data-fa-pagination-step]";
const ELLIPSIS = "[data-fa-pagination-ellipsis]";

const CATEGORY = "data-fa-article-tile-category";
const PER_PAGE = "data-fa-article-listing-per-page";
const RESULTS = "data-fa-article-listing-results";
const EMPTY_MESSAGE = "data-fa-article-listing-empty";
const PAGE_VALUE = "data-fa-pagination-page";
const STEP_VALUE = "data-fa-pagination-step";
const EDGE = "data-fa-pagination-ellipsis";
const CURRENT = "aria-current";
const DISABLED = "aria-disabled";
const HIDDEN = "hidden";

const BLOCK = "fa-article-listing";

/* Windowing to first/last/current±1 leaves page 3 of a five-page run unreachable from page 1,
   so it starts above the seven pages that rule can show whole. Figma draws five, all of them. */
const WINDOW_ABOVE = 7;

export const initArticleListing: ComponentInit = (root) => {
	const filters = root.querySelector<HTMLElement>(FILTERS);
	const list = root.querySelector<HTMLElement>(LIST);

	if (!filters || !list) return;

	const items = [...list.querySelectorAll<HTMLElement>(ITEM)];
	const status = root.querySelector<HTMLElement>(STATUS);
	const pager = root.querySelector<HTMLElement>(PAGER);
	const numbers = pager ? [...pager.querySelectorAll<HTMLElement>(PAGE)] : [];
	const steps = pager ? [...pager.querySelectorAll<HTMLElement>(STEP)] : [];
	const gaps = pager ? [...pager.querySelectorAll<HTMLElement>(ELLIPSIS)] : [];

	const pageItems = new Map(
		numbers.map((button) => [button, button.closest<HTMLElement>("li") ?? button]),
	);

	const declared = Number.parseInt(root.getAttribute(PER_PAGE) ?? "", 10);
	const perPage = Number.isFinite(declared) && declared > 0 ? declared : ARTICLE_LISTING_PER_PAGE;

	const authored = {
		pager: pager?.hasAttribute(HIDDEN) ?? false,
		current: new Map(numbers.map((button) => [button, button.getAttribute(CURRENT)])),
		slots: new Map([...pageItems.values()].map((node) => [node, node.hasAttribute(HIDDEN)])),
		gaps: new Map(gaps.map((gap) => [gap, gap.hasAttribute(HIDDEN)])),
		steps: new Map(steps.map((button) => [button, button.getAttribute(DISABLED)])),
	};

	const listeners = new AbortController();
	const { signal } = listeners;

	let page = 1;
	let pageCount = 1;
	let moved = false;
	let initial = true;

	const inWindow = (value: number) =>
		pageCount <= WINDOW_ABOVE ||
		value === 1 ||
		value === pageCount ||
		Math.abs(value - page) <= 1;

	const renderPager = () => {
		if (!pager) return;

		pager.toggleAttribute(HIDDEN, pageCount <= 1);

		const middle: number[] = [];

		for (const button of numbers) {
			const value = Number(button.getAttribute(PAGE_VALUE));
			const keep = value <= pageCount && inWindow(value);

			pageItems.get(button)?.toggleAttribute(HIDDEN, !keep);

			if (keep && value !== 1 && value !== pageCount) middle.push(value);
			if (value === page) button.setAttribute(CURRENT, "page");
			else button.removeAttribute(CURRENT);
		}

		const lead = middle.length > 0 && Math.min(...middle) > 2;
		const trail = middle.length > 0 && Math.max(...middle) < pageCount - 1;

		for (const gap of gaps)
			gap.toggleAttribute(HIDDEN, gap.getAttribute(EDGE) === "lead" ? !lead : !trail);

		for (const button of steps) {
			const disabled =
				button.getAttribute(STEP_VALUE) === "prev" ? page <= 1 : page >= pageCount;

			button.setAttribute(DISABLED, String(disabled));
		}
	};

	const announce = (matched: readonly HTMLElement[], shown: readonly HTMLElement[]) => {
		if (!status) return;

		const template = root.getAttribute(RESULTS);
		const first = shown[0];

		if (!template || !first) {
			status.textContent = root.getAttribute(EMPTY_MESSAGE) ?? "";
			return;
		}

		const from = matched.indexOf(first) + 1;

		status.textContent = fillTemplate(template, {
			from,
			to: from + shown.length - 1,
			total: matched.length,
		});
	};

	const filter = initFilterList(
		{
			root,
			filters,
			items,
			allValue: ARTICLE_LISTING_ALL,
			categoryAttribute: CATEGORY,
			block: BLOCK,
			inertTarget: list,
			applyVisible: (matched) => {
				pageCount = pageCountFor(matched.length, perPage);
				page = Math.min(page, pageCount);

				const start = (page - 1) * perPage;
				const shown = matched.slice(start, start + perPage);

				for (const item of items) item.toggleAttribute(HIDDEN, !shown.includes(item));

				renderPager();

				return shown;
			},
			onFilterChange: () => {
				page = 1;
				moved = false;
			},
			onApplied: (matched, shown) => {
				if (initial) {
					initial = false;
					return;
				}

				announce(matched, shown);

				if (!moved) return;

				moved = false;
				scrollToElement(list, headerOffset());
			},
		},
		signal,
	);

	pager?.addEventListener(
		"click",
		(event) => {
			const target = event.target;

			if (!(target instanceof Element)) return;

			const number = target.closest<HTMLElement>(PAGE);
			const button = target.closest<HTMLElement>(STEP);
			let next: number;

			if (number) next = Number(number.getAttribute(PAGE_VALUE));
			else if (button) {
				if (button.getAttribute(DISABLED) === "true") return;

				next = page + (button.getAttribute(STEP_VALUE) === "prev" ? -1 : 1);
			} else return;

			next = Math.min(Math.max(next, 1), pageCount);

			if (next === page) return;

			page = next;
			moved = true;
			filter.refresh(true);
		},
		{ signal },
	);

	filter.refresh(false);

	return () => {
		listeners.abort();
		filter.restore();

		pager?.toggleAttribute(HIDDEN, authored.pager);

		for (const [button, value] of authored.current) {
			if (value === null) button.removeAttribute(CURRENT);
			else button.setAttribute(CURRENT, value);
		}

		for (const [node, value] of authored.slots) node.toggleAttribute(HIDDEN, value);
		for (const [gap, value] of authored.gaps) gap.toggleAttribute(HIDDEN, value);

		for (const [button, value] of authored.steps) {
			if (value === null) button.removeAttribute(DISABLED);
			else button.setAttribute(DISABLED, value);
		}

		if (status) status.textContent = "";
	};
};
